import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  PalletRecord,
  PoolClient,
  ProductRecord,
  ScannerContextResponse,
  StoreOption,
  TripLookup,
} from './barcode-scanner.models';

interface ScannerContext {
  context: ScannerContextResponse;
  servicedStores: string[];
  poolClients: PoolClient[];
}

@Injectable({
  providedIn: 'root',
})
export class BarcodeScannerApiService {
  private readonly proxyUrl = '/api/barcode_scanner/barcode_scanner.php';

  constructor(private http: HttpClient) {}

  getContext(): Observable<ScannerContext> {
    return this.proxy<ScannerContextResponse>('context').pipe(
      map((context) => ({
        context,
        servicedStores: this.extractStoreNumbers(context.serviced_stores),
        poolClients: this.asArray<PoolClient>(context.pool_clients)
          .filter((client) => !!client.pool_point_client_uuid),
      }))
    );
  }

  getPoolClients(poolPointUuid: string): Observable<PoolClient[]> {
    return this.proxy<unknown>('poolClients', { pool_point_uuid: poolPointUuid }).pipe(
      map((response) => this.asArray<PoolClient>(response).filter((client) => !!client.pool_point_client_uuid))
    );
  }

  getStoreNumbers(tripListUuid: string, poolPointUuid: string): Observable<StoreOption[]> {
    return this.proxy<unknown>('storeNumbers', {
      trip_list_uuid: tripListUuid,
      pool_point_uuid: poolPointUuid,
    }).pipe(
      map((response) => this.asArray<StoreOption>(response).filter((store) => !!store.store_number))
    );
  }

  getTripByAsn(asnNumber: string, poolPointUuid: string): Observable<TripLookup[]> {
    return this.proxy<unknown>('tripByAsn', {
      asn_number: asnNumber,
      pool_point_uuid: poolPointUuid,
    }).pipe(map((response) => this.asArray<TripLookup>(response)));
  }

  getShipmentByAsn(asnNumber: string): Observable<TripLookup[]> {
    return this.proxy<unknown>('shipmentByAsn', { asn_number: asnNumber }).pipe(
      map((response) => this.asArray<TripLookup>(response))
    );
  }

  generateTripNumber(asnNumber: string, poolPointUuid: string): Observable<TripLookup> {
    return this.proxy<TripLookup>('generateTripNumber', {
      asn_number: asnNumber,
      pool_point_uuid: poolPointUuid,
    });
  }

  generateAsnAndTripNumber(asnNumber: string, poolPointUuid: string): Observable<TripLookup> {
    const params: Record<string, unknown> = { pool_point_uuid: poolPointUuid };

    if (asnNumber.trim()) {
      params['asn_number'] = asnNumber.trim();
    }

    return this.proxy<TripLookup>('generateAsnAndTripNumber', params);
  }

  fetchAsnFromBarcodes(barcodes: string[]): Observable<TripLookup> {
    return this.proxy<TripLookup>('fetchAsnFromBarcodes', {}, { barcodes });
  }

  getTripList(tripListUuid: string): Observable<TripLookup> {
    return this.proxy<TripLookup>('tripList', { trip_list_uuid: tripListUuid });
  }

  getProducts(params: Record<string, unknown>): Observable<ProductRecord[]> {
    return this.proxy<unknown>('products', params).pipe(
      map((response) => this.asArray<ProductRecord>(response))
    );
  }

  createProduct(body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('createProduct', {}, body);
  }

  updateProduct(productUuid: string, body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('updateProduct', { product_uuid: productUuid }, body);
  }

  handleBarcodeScan(body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('handleBarcodeScan', {}, body);
  }

  updateReturnStatus(body: Record<string, unknown>, scanType?: 'blind'): Observable<Record<string, unknown>> {
    const params: Record<string, unknown> = {};

    if (scanType) {
      params['scan_type'] = scanType;
    }

    return this.proxy<Record<string, unknown>>('updateReturnStatus', params, body);
  }

  insertOverageBarcode(body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('insertOverageBarcode', {}, body);
  }

  generateBarcode(tripListUuid: string, storeNumber: string): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('generateBarcode', {
      trip_list_uuid: tripListUuid,
      store_number: storeNumber,
    });
  }

  getPalletList(tripListUuid: string, storeNumber?: string): Observable<PalletRecord[]> {
    const params: Record<string, unknown> = { trip_list_uuid: tripListUuid };

    if (storeNumber) {
      params['store_number'] = storeNumber;
    }

    return this.proxy<unknown>('palletList', params).pipe(
      map((response) => this.asArray<PalletRecord>(response))
    );
  }

  createPallet(body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('createPallet', {}, body);
  }

  updatePackage(dimensionUuid: string, body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('updatePackage', { dimension_uuid: dimensionUuid }, body);
  }

  addCartonToPallet(body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('addCartonToPallet', {}, body);
  }

  removeProductFromPackage(body: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.proxy<Record<string, unknown>>('removeProductFromPackage', {}, body);
  }

  extractStoreNumber(barcode: string): string {
    const trimmed = barcode.trim();
    const firstAlphaIndex = trimmed.search(/[A-Za-z]/);

    if (firstAlphaIndex > 0) {
      return trimmed.slice(0, firstAlphaIndex);
    }

    return trimmed.slice(0, 5);
  }

  normalizeBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      return ['true', 't', '1', 'yes', 'y'].includes(value.trim().toLowerCase());
    }

    return false;
  }

  responseMessage(response: Record<string, unknown>): string {
    const message = response['message'];
    const status = response['status'];

    if (typeof status === 'string' && status.trim()) {
      return status;
    }

    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    return 'OK';
  }

  asArray<T>(value: unknown): T[] {
    if (Array.isArray(value)) {
      return value as T[];
    }

    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const candidates = ['data', 'rows', 'results', 'items', 'products', 'pallets', 'pool_clients', 'stores'];

      for (const key of candidates) {
        if (Array.isArray(record[key])) {
          return record[key] as T[];
        }
      }
    }

    return [];
  }

  private proxy<T>(action: string, params: Record<string, unknown> = {}, body: Record<string, unknown> = {}): Observable<T> {
    return this.http.post<T>(this.proxyUrl, {
      action,
      params,
      body,
    });
  }

  private extractStoreNumbers(value: unknown): string[] {
    if (typeof value === 'string') {
      return this.uniqueStoreNumbers(value.split(','));
    }

    const rows = this.asArray<Record<string, unknown>>(value);
    const storeNumbers = rows
      .map((row) => {
        const candidate = row['store_number'] || row['pool_point_store_number'] || row['storeNumber'] || row['number'];
        return typeof candidate === 'string' || typeof candidate === 'number' ? String(candidate) : '';
      });

    return this.uniqueStoreNumbers(storeNumbers);
  }

  private uniqueStoreNumbers(values: string[]): string[] {
    return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).sort();
  }
}
